import { common } from '@/features/i18n/common';
import { tryCatchSync } from '@/try-catch';
import type {
  ChecklistVerdict,
  ComparisonCellPrice,
  ComparisonGridOffer,
  DisciplineSelection,
  LegacyScopeItem,
  ProcurementTemplate,
  SuggestedDiscipline,
  WorkPackageScopeRow,
} from '@/features/subcontractor-bids/types';
import type { KgReason, KgStatusResponse } from '@/features/documents/types';
import { formatNumber, formatNumberToParts } from '@/number';
import type { ScopeAgentStatus } from '@/features/scope-breakdown/types';
import { scopeSheetState } from '@/features/scope-breakdown/utils';

export const scopeItemRow = (item: WorkPackageScopeRow | LegacyScopeItem): WorkPackageScopeRow => {
  if ('tabId' in item) return item;
  const sources = item.sources?.length ? item.sources : item.source ? [item.source] : [];
  return {
    id: item.id,
    tabId: '',
    tab: item.chapterPath?.join(' › ') ?? '',
    parentId: null,
    group: false,
    code: null,
    kind: 'post',
    title: item.subjectLabel,
    scope: item.scopeStatement ?? null,
    description: item.description,
    quantity: item.quantityValue,
    unit: item.quantityUnit,
    location: item.location,
    materials: item.materials,
    severity: item.severity,
    package: null,
    discipline: item.discipline,
    status: null,
    citedStandards: item.citedStandards,
    sources: sources.map((source) => ({
      documentId: source.docId,
      page: source.page,
      quote: source.sourceSpan ?? '',
      recordKey: null,
    })),
  };
};

export const checklistVerdictToAnswer = (verdict: ChecklistVerdict): boolean | null =>
  verdict === 'yes' ? true : verdict === 'no' ? false : null;

export const canCreateWorkPackageAgainstKg = (
  status: Pick<KgStatusResponse, 'wpReady' | 'reason'> | undefined,
) => status?.wpReady === true && status?.reason !== 'no_documents';

export const packCreationBlocker = (
  kgReason: KgReason | undefined,
  scope: ScopeAgentStatus | undefined,
) => {
  if (kgReason === 'no_documents') return common.noDocumentsUploaded;
  if (kgReason === 'indexing') return common.waitForDocumentsIndexing;
  const sheet = scopeSheetState(scope);
  if (sheet) return common.scopeSheet[sheet];
  if (scope?.artifactId) return common.scopeSheet.noPackages;
  return common.waitForKeyInformation;
};

export const scopeSheetPrompt = (
  kgReason: KgReason | undefined,
  scope: ScopeAgentStatus | undefined,
) => {
  const sheet = scopeSheetState(scope);
  if (sheet === 'missing' && (kgReason === 'no_documents' || kgReason === 'indexing')) return null;
  return sheet;
};

type VatLabels = {
  exclVat: string;
  inclVat: string;
  vatUnknown: string;
};

const formatVatSuffix = (status: string | null | undefined, labels?: VatLabels) => {
  if (!labels || !status) return '';
  if (status === 'EXCL_VAT') return labels.exclVat;
  if (status === 'INCL_VAT') return labels.inclVat;
  if (status === 'UNKNOWN') return labels.vatUnknown;
  return '';
};

export const formatOfferPriceParts = (
  offer: Pick<ComparisonGridOffer, 'totalPrice' | 'totalPriceCurrency' | 'totalPriceVatStatus'>,
  vatLabels?: VatLabels,
) => {
  if (offer.totalPrice == null) return null;
  const vat = formatVatSuffix(offer.totalPriceVatStatus, vatLabels);
  if (!offer.totalPriceCurrency) {
    return { amount: formatNumber(offer.totalPrice), currency: '', vat };
  }
  const parts = formatNumberToParts(offer.totalPrice, {
    style: 'currency',
    currency: offer.totalPriceCurrency,
    maximumFractionDigits: 0,
  });
  const amount = parts
    .filter((part) => part.type !== 'currency')
    .map((part) => part.value)
    .join('')
    .trim();
  const currency = parts.find((part) => part.type === 'currency')?.value ?? '';
  return { amount, currency, vat };
};

export const formatOfferPrice = (
  offer: Pick<ComparisonGridOffer, 'totalPrice' | 'totalPriceCurrency' | 'totalPriceVatStatus'>,
  vatLabels?: VatLabels,
) => {
  const price = formatOfferPriceParts(offer, vatLabels);
  if (!price) return null;
  return [price.amount, price.currency, price.vat].filter(Boolean).join(' ');
};

export const formatCellPrice = (price: ComparisonCellPrice | null | undefined) => {
  if (price?.amount == null) return null;
  const { amount, amountMax, currency } = price;
  const options: Intl.NumberFormatOptions = currency
    ? { style: 'currency', currency, maximumFractionDigits: 0 }
    : { maximumFractionDigits: 0 };
  if (amountMax == null || amountMax === amount) return formatNumber(amount, options);
  return new Intl.NumberFormat('nb-NO', options).formatRange(
    Math.min(amount, amountMax),
    Math.max(amount, amountMax),
  );
};

export const prettifyDiscipline = (slug: string) => {
  return slug
    .split(/[_\s]+/)
    .map((word) =>
      word.length <= 3
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');
};

export const summarizeSections = (sections: (string | null)[]) => {
  const distinct = [...new Set(sections.filter((section): section is string => !!section))];
  if (distinct.length === 0) return null;
  const visible = distinct.slice(0, 3).join(', ');
  return {
    text: distinct.length > 3 ? `${visible} +${distinct.length - 3}` : visible,
    title: distinct.join('\n'),
  };
};

export const formatQuantity = (
  value: number | null | undefined,
  unit: string | null | undefined,
) => {
  if (value == null) return null;
  return `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;
};

type DisciplineGroup = {
  slug: string | null;
  label: string;
  items: SuggestedDiscipline[];
};

const byDisplayName = (a: SuggestedDiscipline, b: SuggestedDiscipline) =>
  a.displayName.localeCompare(b.displayName);

export const groupDisciplinesByTemplate = (
  disciplines: SuggestedDiscipline[],
  templates: ProcurementTemplate[],
  otherLabel: string,
  templateScopeCounts: Record<string, Record<string, number>> = {},
): DisciplineGroup[] => {
  const bySlug = new Map(disciplines.map((d) => [d.discipline, d]));
  const groups: DisciplineGroup[] = [];
  for (const template of templates) {
    const templateCounts = templateScopeCounts[template.templateKey];
    const items = template.disciplines
      .map((ref) => bySlug.get(ref.slug))
      .filter((d): d is SuggestedDiscipline => d != null)
      .map((discipline) => ({
        ...discipline,
        scopeCount: templateCounts
          ? (templateCounts[discipline.discipline] ?? 0)
          : discipline.scopeCount,
      }))
      .sort(byDisplayName);
    if (items.length > 0) {
      groups.push({ slug: template.templateKey, label: template.label, items });
    }
  }
  groups.sort((a, b) => a.label.localeCompare(b.label));
  const covered = new Set(groups.flatMap((group) => group.items.map((d) => d.discipline)));
  const rest = disciplines.filter((d) => !covered.has(d.discipline)).sort(byDisplayName);
  if (rest.length > 0) groups.push({ slug: null, label: otherLabel, items: rest });
  return groups;
};

export const groupsMatchingSearch = (groups: DisciplineGroup[], search: string) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return groups;
  return groups
    .map((group) =>
      group.label.toLowerCase().includes(needle)
        ? group
        : {
            ...group,
            items: group.items.filter(
              (d) =>
                d.displayName.toLowerCase().includes(needle) ||
                d.description?.toLowerCase().includes(needle),
            ),
          },
    )
    .filter((group) => group.items.length > 0);
};

export const withGroupSelection = (
  selection: DisciplineSelection,
  groupSlugs: string[],
  nextSlugs: string[],
): DisciplineSelection => ({
  disciplines: [
    ...selection.disciplines.filter((slug) => !groupSlugs.includes(slug)),
    ...nextSlugs,
  ],
});

export const languageName = (locale: string) => {
  const base = locale.split('-')[0] ?? locale;
  return (
    tryCatchSync(() => new Intl.DisplayNames([locale], { type: 'language' }).of(base)).data ?? base
  );
};
